import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  type ReportId,
  ReportIds,
  ReportInputParameterKeys,
  reportSupportsInputParameter,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import {
  ReportDefaultSettings,
  ReportDefaultSettingsKeys,
} from 'ag-common-lib/lib/models/reporting/report-default-settings.model';
import { BaseFormService } from '../../../utils/base-form-service';
import { ReportDefaultSettingsService } from '../../../services/report-default-settings.service';
import { PRODUCTION_PRECONFIGURED_REPORTS } from '../../../components/production-report-viewer-page/reports-core/production-reports.config';
import { PRODUCTION_REPORT_INCLUDE_THRESHOLD_DEFAULT } from '../../../components/production-report-viewer-page/reports-core/production-reports.config.shared';
import { ReportDefaultSettingsFormData, ReportDefaultSettingsFormKeys } from './report-default-settings-form.model';

@Injectable()
export class ReportDefaultSettingsFormService extends BaseFormService<ReportDefaultSettingsFormData> {
  private reportId: ReportId | null = null;

  private readonly reportDefaultSettingsService = inject(ReportDefaultSettingsService);
  private readonly toastrService = inject(ToastrService);

  setReportId(reportId: ReportId): void {
    this.reportId = reportId;
  }

  clearReportId(): void {
    this.reportId = null;
  }

  getFormData = (data?: Partial<ReportDefaultSettingsFormData>): ReportDefaultSettingsFormData => {
    this.formChangesDetector.clear();
    const initialData = Object.assign({}, new ReportDefaultSettingsFormData(), data);

    const formData = new Proxy(initialData, {
      set: (target, prop, value, receiver) => {
        const prevValue = target[prop as keyof ReportDefaultSettingsFormData];

        if (value !== prevValue) {
          this.formChangesDetector.handleChange(prop, value, prevValue);
          Reflect.set(target, prop, value, receiver);
          this.formDataSig.update(current => current);
        }

        return true;
      },
    });

    this.formData = formData;
    this.formDataSig.set(formData);

    return formData;
  };

  loadSettings = async (): Promise<boolean> => {
    if (!this.reportId) {
      return false;
    }

    this.startLoadData();

    try {
      const settings = await this.loadReportDefaultSettings(this.reportId);
      this.getFormData(this.mapToFormData(this.reportId, settings));
      return !!settings?.[ReportDefaultSettingsKeys.period];
    } catch {
      this.toastrService.error('Unable to load report default settings.');
      this.getFormData(this.mapToFormData(this.reportId, null));
      return false;
    } finally {
      this.stopLoadData();
    }
  };

  handleSave = async (): Promise<boolean> => {
    if (!this.reportId) {
      return false;
    }

    const formData = this.formDataSig();
    this.startProgress();

    try {
      await this.reportDefaultSettingsService.save({
        [ReportDefaultSettingsKeys.reportId]: this.reportId as ReportIds,
        ...(reportSupportsInputParameter(this.reportId, ReportInputParameterKeys.ntpDisplayMode)
          ? { [ReportDefaultSettingsKeys.ntpDisplayMode]: formData[ReportDefaultSettingsFormKeys.ntpDisplayMode] }
          : {}),
        ...(reportSupportsInputParameter(this.reportId, ReportInputParameterKeys.showSuppressedAgents)
          ? { [ReportDefaultSettingsKeys.showSuppressedAgents]: formData[ReportDefaultSettingsFormKeys.showSuppressedAgents] }
          : {}),
        [ReportDefaultSettingsKeys.showTotals]: formData[ReportDefaultSettingsFormKeys.showTotals],
        [ReportDefaultSettingsKeys.period]: formData[ReportDefaultSettingsFormKeys.period],
        [ReportDefaultSettingsKeys.title]: formData[ReportDefaultSettingsFormKeys.title],
        [ReportDefaultSettingsKeys.subtitle]: formData[ReportDefaultSettingsFormKeys.subtitle],
        [ReportDefaultSettingsKeys.displayThreshold]: formData[ReportDefaultSettingsFormKeys.displayThreshold],
        [ReportDefaultSettingsKeys.includeThreshold]:
          formData[ReportDefaultSettingsFormKeys.includeThreshold] !== false,
        [ReportDefaultSettingsKeys.rowsPerPage]: formData[ReportDefaultSettingsFormKeys.rowsPerPage],
        [ReportDefaultSettingsKeys.startDate]:
          formData[ReportDefaultSettingsFormKeys.period] === 'custom'
            ? formData[ReportDefaultSettingsFormKeys.startDate]
            : '',
        [ReportDefaultSettingsKeys.endDate]:
          formData[ReportDefaultSettingsFormKeys.period] === 'custom'
            ? formData[ReportDefaultSettingsFormKeys.endDate]
            : '',
        [ReportDefaultSettingsKeys.isPublic]: formData[ReportDefaultSettingsFormKeys.isPublic],
      });
      this.formChangesDetector.clear();

      return true;
    } catch {
      this.toastrService.error('Unable to save report default settings. Please try again.');

      return false;
    } finally {
      this.stopProgress();
    }
  };

  private loadReportDefaultSettings = async (reportId: ReportId): Promise<ReportDefaultSettings | null> => {
    try {
      return await this.reportDefaultSettingsService.getByReportId(reportId);
    } catch {
      return null;
    }
  };

  /**
   * Builds modal form data to match `settingsFormDataSig` in production-report-view:
   * baseline from the preconfigured report model, then overlay saved default settings when present.
   */
  private mapToFormData(
    reportId: ReportId,
    settings: ReportDefaultSettings | null,
  ): Partial<ReportDefaultSettingsFormData> {
    const baseline = this.buildBaselineFormData(reportId);
    if (!settings) {
      return baseline;
    }

    return {
      [ReportDefaultSettingsFormKeys.period]: settings[ReportDefaultSettingsKeys.period] ?? 'custom',
      [ReportDefaultSettingsFormKeys.ntpDisplayMode]:
        settings[ReportDefaultSettingsKeys.ntpDisplayMode] ??
        (settings[ReportDefaultSettingsKeys.showNtpSeparately] === false ? 'general_list' : 'separate'),
      [ReportDefaultSettingsFormKeys.showSuppressedAgents]:
        settings[ReportDefaultSettingsKeys.showSuppressedAgents] ??
        (settings[ReportDefaultSettingsKeys.hideSuppressedAgents] === false),
      [ReportDefaultSettingsFormKeys.showTotals]: settings[ReportDefaultSettingsKeys.showTotals] ?? true,
      [ReportDefaultSettingsFormKeys.title]: settings[ReportDefaultSettingsKeys.title] ?? baseline[ReportDefaultSettingsFormKeys.title],
      [ReportDefaultSettingsFormKeys.subtitle]:
        settings[ReportDefaultSettingsKeys.subtitle] ?? baseline[ReportDefaultSettingsFormKeys.subtitle],
      [ReportDefaultSettingsFormKeys.displayThreshold]:
        settings[ReportDefaultSettingsKeys.displayThreshold] ??
        baseline[ReportDefaultSettingsFormKeys.displayThreshold],
      [ReportDefaultSettingsFormKeys.includeThreshold]:
        settings[ReportDefaultSettingsKeys.includeThreshold] ??
        baseline[ReportDefaultSettingsFormKeys.includeThreshold],
      [ReportDefaultSettingsFormKeys.rowsPerPage]:
        settings[ReportDefaultSettingsKeys.rowsPerPage] ?? baseline[ReportDefaultSettingsFormKeys.rowsPerPage],
      [ReportDefaultSettingsFormKeys.startDate]:
        settings[ReportDefaultSettingsKeys.startDate] ?? baseline[ReportDefaultSettingsFormKeys.startDate],
      [ReportDefaultSettingsFormKeys.endDate]:
        settings[ReportDefaultSettingsKeys.endDate] ?? baseline[ReportDefaultSettingsFormKeys.endDate],
      [ReportDefaultSettingsFormKeys.isPublic]:
        settings[ReportDefaultSettingsKeys.isPublic] ?? baseline[ReportDefaultSettingsFormKeys.isPublic],
    };
  }

  private buildBaselineFormData(reportId: ReportId): Partial<ReportDefaultSettingsFormData> {
    const report = PRODUCTION_PRECONFIGURED_REPORTS.find(item => item.id === reportId);
    const displayThresholdDefault = report?.variables.find(
      variable => variable.name === ReportInputParameterKeys.displayThreshold,
    )?.defaultValue;

    return {
      // Matches cloneTemplate() + settingsFormDataSig title/subtitle after selectReport
      [ReportDefaultSettingsFormKeys.title]: '',
      [ReportDefaultSettingsFormKeys.subtitle]: '',
      [ReportDefaultSettingsFormKeys.rowsPerPage]: report?.template.rowsPerPage ?? 1,
      [ReportDefaultSettingsFormKeys.displayThreshold]: this.toFiniteNumber(displayThresholdDefault, 0),
      [ReportDefaultSettingsFormKeys.includeThreshold]: PRODUCTION_REPORT_INCLUDE_THRESHOLD_DEFAULT,
      [ReportDefaultSettingsFormKeys.startDate]: '',
      [ReportDefaultSettingsFormKeys.endDate]: '',
      [ReportDefaultSettingsFormKeys.isPublic]: false,
    };
  }

  private toFiniteNumber(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (value === undefined || value === null || value === '') {
      return fallback;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return fallback;
    }

    return numericValue;
  }
}
